var _ = require('underscore');
var TorsoFormModel = require('backbone-torso/modules/FormModel');
var stringUtils = require('./../utils/stringUtils');
var emailUtils = require('./../utils/emailUtils');

var SSN_REGEX = new RegExp('[0-9]{3}-[0-9]{2}-[0-9]{4}');

/**
 * Form model for patient signup.
 *
 * @class PatientSignupFormModel
 * @constructor
 */
module.exports = TorsoFormModel.extend({
  /**
   * @property validation {Object} The validation rules for this form model
   */
  validation: {
    firstName: {
      requiredField: {
        shouldValidate: '_shouldValidateNonDobData'
      },
    },
    lastName: {
      requiredField: {
        shouldValidate: '_shouldValidateNonDobData'
      },
    },
    localDob: {
      required: true
    },
    ssn: {
      requiredField: {
        shouldValidate: '_shouldValidateNonDobData'
      },
      fn: function(ssn) {
        if (!this._shouldValidateNonDobData()) {
          return '';
        }
        if (!SSN_REGEX.test(ssn)) {
          return {
            key: 'error.invalid'
          };
        }
      }
    },
    username: {
      requiredField: {
        shouldValidate: '_shouldValidateAdditionalFields'
      },
      fn: function(username) {
        if (!this._shouldValidateAdditionalFields()) {
          return '';
        }
        if (username && username.length < 6) {
          return {
            key: 'error.invalid'
          };
        }
      }
    },
    newPassword:{
      requiredField: {
        shouldValidate: '_shouldValidateAdditionalFields'
      }
    },
    confirmPassword:{
      requiredField: {
        shouldValidate: '_shouldValidateAdditionalFields'
      },
      fn: function(confirmPassword) {
        if (!this._shouldValidateAdditionalFields() || !confirmPassword) {
          return '';
        }
        var password = this.get('newPassword');
        if (confirmPassword && confirmPassword !== password) {
          return {
            key: 'error.match'
          };
        }
      }
    },
    emailAddress: {
      requiredField: {
        shouldValidate: '_shouldValidateAdditionalFields'
      },
      fn: function(email) {
        if (!this._shouldValidateAdditionalFields()) {
          return '';
        }
        if (!emailUtils.isEmailFormat(email)) {
          return {
            key: 'error.invalid'
          };
        }
      }
    },
    confirmEmailAddress: {
      requiredField: {
        shouldValidate: '_shouldValidateAdditionalFields'
      },
      fn: function(confirmEmail) {
        if (!this._shouldValidateAdditionalFields() || !confirmEmail) {
          return '';
        }
        if (!emailUtils.isEmailFormat(confirmEmail)) {
          return {
            key: 'error.invalid'
          };
        }
        var email = this.get('emailAddress');
        if (confirmEmail && confirmEmail !== email) {
          return {
            key: 'error.match'
          };
        }
      }
    },
    securityQuestion1:{
      requiredField: {
        shouldValidate: '_shouldValidateAdditionalFields'
      }
    },
    securityAnswer1:{
      requiredField: {
        shouldValidate: '_shouldValidateAdditionalFields'
      }
    },
    securityQuestion2:{
      requiredField: {
        shouldValidate: '_shouldValidateAdditionalFields'
      },
      fn: function(securityQuestion2) {
        if (!this._shouldValidateAdditionalFields() || !securityQuestion2) {
          return '';
        }
        var securityQuestion1 = this.get('securityQuestion1');
        if (securityQuestion1 && securityQuestion1 === securityQuestion2) {
          return {
            key: 'error.unique'
          };
        }
      }
    },
    securityAnswer2:{
      requiredField: {
        shouldValidate: '_shouldValidateAdditionalFields'
      }
    },
    agreedToAdditionalForm:{
      fn: function(agreed) {
        if (!this._shouldValidateAdditionalForm()) {
          return '';
        }
        if (!agreed) {
          return {
            key: 'error.decline'
          };
        }
      }
    },
    agreedToPrivacyForm:{
      fn: function(agreed) {
        if (!this._shouldValidateAdditionalFields()) {
          return '';
        }
        if (!agreed) {
          return {
            key: 'error.decline'
          };
        }
      }
    },
    agreedToDisclaimer:{
      fn: function(agreed) {
        if (!this._shouldValidateAdditionalFields()) {
          return '';
        }
        if (!agreed) {
          return {
            key: 'error.decline'
          };
        }
      }
    }
  },

  /**
   * @method _shouldValidateNonDobData
   * @returns {Boolean} true if the non-dob data should be validated.
   * @private
   */
  _shouldValidateNonDobData: function() {
    return !this.get('inviteId');
  },

  /**
   * @method _shouldValidateAdditionalFields
   * @returns {Boolean} true if the additional data should be validated.
   * @private
   */
  _shouldValidateAdditionalFields: function() {
    return this.get('authenticated');
  },

  /**
   * @method _shouldValidateAdditionalForm
   * @returns {Boolean} true if the additional form should be validated.
   * @private
   */
  _shouldValidateAdditionalForm: function() {
    return this._shouldValidateAdditionalFields && !!this.get('additionalEnrollmentFormVecnaId');
  },

  /**
   * The mappings to use when an api user model is bound to this form model.
   * This names some sub-mappings that allow using __pull() and __push() to interact with a subset of the feature properties.
   * @property mapping {Object}
   */
  mapping: {
    formFeatureConfigModel: 'additionalEnrollmentFormVecnaId',
    patientRegistrationModel: 'vecnaId name localDob ssn inviteId username newPassword confirmPassword emailAddress confirmEmailAddress securityQuestion1 securityAnswer1 securityQuestion2 securityAnswer2 agreedToAdditionalForm agreedToPrivacyForm agreedToDisclaimer',
    name: {
      patientRegistrationModel: 'name',
      pull: function(models) {
        var name = models.patientRegistrationModel.name;
        var givenName = name && name.givenName;
        var familyName = name && name.familyName;
        this.set('firstName', givenName);
        this.set('lastName', familyName);
      },
      push: function(models) {
        var patientRegistrationModel = models.patientRegistrationModel;
        var firstName = this.get('firstName');
        var lastName = this.get('lastName');
        var name = {};
        name.givenName = firstName;
        name.familyName = lastName;
        patientRegistrationModel.set('name', name);
      }
    },
    ssn: {
      patientRegistrationModel: 'ssn',
      pull: function(models) {
        var ssn = models.patientRegistrationModel.ssn;
        var first3 = ssn && ssn.first3;
        var middle2 = ssn && ssn.middle2;
        var last4 = ssn && ssn.last4;
        var ssnToPull = _.filter([first3, middle2, last4]).join('-');
        this.set('ssn', ssnToPull);
      },
      push: function(models) {
        var patientRegistrationModel = models.patientRegistrationModel;
        var ssn = this.get('ssn');
        var ssnParts = ssn && ssn.split('-');
        var ssnToPush = {};
        ssnToPush.first3 = ssn && ssnParts[0];
        ssnToPush.middle2 = ssn && ssnParts[1];
        ssnToPush.last4 = ssn && ssnParts[2];
        patientRegistrationModel.set('ssn', ssnToPush);
      }
    }
  },

  /**
   * Format the ssn field. Remove all '-' and add '-' in appropriate place.
   * @method formatSsn
   */
  formatSsn: function() {
    var ssn = this.get('ssn');
    ssn = stringUtils.nullSafeReplaceAllSpaces(ssn);
    ssn = ssn.replace(/-/g, '');
    var first3 = ssn.substring(0, 3);
    var middle2 = ssn.substring(3, 5);
    var last4 = ssn.substring(5, 9);
    var ssnAfterFormat = _.filter([first3, middle2, last4]).join('-');
    this.set('ssn', ssnAfterFormat);
  }
});
